import { useSyncExternalStore, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useUser } from '@/contexts/UserContext'
import { findDestination } from '@/config/navigationConfig'

/**
 * Recently visited navigation destinations (by href), most-recent first, capped.
 * Persisted in localStorage and shared via an external store so the sidebar and
 * palette update live.
 */
const KEY = 'nav-recents'
const MAX = 5
const EMPTY: string[] = []
const listeners = new Set<() => void>()
let cache: string[] | null = null

function read(): string[] {
  if (cache === null) {
    try {
      const raw = localStorage.getItem(KEY)
      const parsed = raw ? JSON.parse(raw) : []
      cache = Array.isArray(parsed) ? parsed.filter((h) => typeof h === 'string') : []
    } catch {
      cache = []
    }
  }
  return cache
}

function write(next: string[]) {
  cache = next
  try {
    localStorage.setItem(KEY, JSON.stringify(next))
  } catch {
    /* ignore quota/availability errors */
  }
  listeners.forEach((l) => l())
}

function subscribe(cb: () => void) {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === KEY) {
      cache = null
      listeners.forEach((l) => l())
    }
  })
}

/** Record a visit to a destination href (move-to-front, deduped, capped). */
export function recordRecent(href: string) {
  const current = read()
  if (current[0] === href) return
  write([href, ...current.filter((h) => h !== href)].slice(0, MAX))
}

/** The list of recent destination hrefs. */
export function useRecentRoutes(): string[] {
  return useSyncExternalStore(subscribe, read, () => EMPTY)
}

/**
 * Records the current route as a recent destination. Call once (e.g. in Layout).
 * Only known nav destinations are recorded, so the list stays meaningful.
 */
export function useRecordRecentRoute() {
  const location = useLocation()
  const { effectiveUserType } = useUser()

  useEffect(() => {
    const dest = findDestination(location.pathname, effectiveUserType)
    if (dest) recordRecent(dest.href)
  }, [location.pathname, effectiveUserType])
}
